import type { CashbackEntryTypeValue, CashbackRuleInput } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { PaginationMeta } from '@/modules/auth/types';

export const CASHBACK_ROUTES = {
  RULES: '/cashback/regras',
  ENTRIES: '/cashback/extrato',
} as const;

const RULES = '/api/v1/cashback/rules';
const ENTRIES = '/api/v1/cashback/entries';
const CUSTOMERS = '/api/v1/cashback/customers';

export interface CashbackRule {
  id: string;
  companyId: string;
  name: string;
  percent: number;
  serviceId: string | null;
  serviceName: string | null;
  unitId: string | null;
  unitName: string | null;
  weekdays: number[];
  startMinutes: number | null;
  endMinutes: number | null;
  validityDays: number;
  minAmount: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CashbackEntry {
  id: string;
  companyId: string;
  customerId: string;
  customerName: string;
  customerImage: string | null;
  type: CashbackEntryTypeValue;
  amount: number;
  usedAmount: number;
  availableAmount: number;
  expiresAt: string | null;
  isExpired: boolean;
  appointmentId: string | null;
  createdAt: string;
}

export interface CashbackBalance {
  earned: number;
  used: number;
  active: number;
  expired: number;
}

export interface CashbackCustomerSummary extends CashbackBalance {
  totalSpent: number;
}

export interface RulesQuery {
  page: number;
  perPage?: number;
  search?: string;
  status?: 'all' | 'active' | 'inactive';
}

export interface EntriesQuery {
  page: number;
  perPage?: number;
  type?: CashbackEntryTypeValue;
  customerId?: string;
  from?: string;
  to?: string;
}

export const cashbackApi = {
  listRules(params: RulesQuery) {
    const query = new URLSearchParams({ page: String(params.page) });
    if (params.perPage) query.set('perPage', String(params.perPage));
    if (params.search) query.set('search', params.search);
    if (params.status && params.status !== 'all') query.set('status', params.status);

    return apiClient.get<{ data: CashbackRule[]; meta: PaginationMeta }>(
      `${RULES}?${query.toString()}`,
    );
  },

  createRule: (input: CashbackRuleInput) => apiClient.post<CashbackRule>(RULES, input),
  updateRule: (id: string, input: Partial<CashbackRuleInput>) =>
    apiClient.patch<CashbackRule>(`${RULES}/${id}`, input),
  removeRule: (id: string) => apiClient.delete<{ message: string }>(`${RULES}/${id}`),

  listEntries(params: EntriesQuery) {
    const query = new URLSearchParams({ page: String(params.page) });
    if (params.perPage) query.set('perPage', String(params.perPage));
    if (params.type) query.set('type', params.type);
    if (params.customerId) query.set('customerId', params.customerId);
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);

    return apiClient.get<{
      data: CashbackEntry[];
      meta: PaginationMeta;
      totals: CashbackBalance;
    }>(`${ENTRIES}?${query.toString()}`);
  },

  customerSummary: (customerId: string) =>
    apiClient.get<CashbackCustomerSummary>(`${CUSTOMERS}/${customerId}`),
};

const KEYS = {
  all: ['cashback'] as const,
  rules: ['cashback', 'rules'] as const,
  entries: ['cashback', 'entries'] as const,
  customer: (id: string) => ['cashback', 'customer', id] as const,
};

export function useCashbackRules(params: RulesQuery) {
  return useQuery({
    queryKey: [...KEYS.rules, params],
    queryFn: () => cashbackApi.listRules(params),
    placeholderData: (previous) => previous,
  });
}

export function useCashbackEntries(params: EntriesQuery) {
  return useQuery({
    queryKey: [...KEYS.entries, params],
    queryFn: () => cashbackApi.listEntries(params),
    placeholderData: (previous) => previous,
  });
}

/** Saldo do cliente: aparece na ficha dele e no resumo do agendamento. */
export function useCashbackCustomer(customerId: string | undefined) {
  return useQuery({
    queryKey: KEYS.customer(customerId ?? ''),
    queryFn: () => cashbackApi.customerSummary(customerId as string),
    enabled: Boolean(customerId),
  });
}

function useCashbackMutation<TArgs>(mutationFn: (args: TArgs) => Promise<unknown>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useCreateCashbackRule() {
  return useCashbackMutation((input: CashbackRuleInput) => cashbackApi.createRule(input));
}

export function useUpdateCashbackRule() {
  return useCashbackMutation(({ id, input }: { id: string; input: Partial<CashbackRuleInput> }) =>
    cashbackApi.updateRule(id, input),
  );
}

export function useDeleteCashbackRule() {
  return useCashbackMutation((id: string) => cashbackApi.removeRule(id));
}
